// actual place for calculations

import { LINE_ITEM_CATEGORIES, LINE_ITEM_CODES, PAYMENT_CHOICES } from '../constants';

interface LineItem {
	code: string;
	description: string;
	amount: number;
	quantity?: number;
	metadata?: Record<string, any>;
}

interface Categories {
	[categoryKey: string]: {
		[groupKey: string]: LineItem[];
	};
}

interface VehicleCost {
	preDiscountTotalPrice: number;
	perVehicleDiscounts: Array<{ discountId: string; amount: number }>;
	totalPrice: number;
	basePrice: number;
	specs: number;
	accessories: number;
	boltOnAccessories: number;
	shippingFee: number;
	pickUpFee: number;
	tempTagFee: number;
	totalPossibleRegistrationFees: number;
	salesTax: number;
	discounts: number;
	downPayment: number;
	perVehiclePrevPaymentSum: number;
	orderFee: number;
	vipReservationFee: number;
	otherFees: number;
	defaultFees: number;
}

interface LooseAccessoryCost {
	preDiscountTotalPrice: number;
	totalPrice: number;
	basePrice: number;
	accessorySalesTax: number;
	discounts: number;
	appliedDiscounts: Array<{ discountId: string; amount: number }>;
}

interface AccessoryCost {
	preDiscountTotalPrice: number;
	totalPrice: number;
}

interface Totals {
	totalShippingFees: number;
	totalPickUpFees: number;
	totalTaxes: number;
	totalLooseAccessoryTaxes: number;
	totalRegistrationFees: number;
	totalTempTagFees: number;
	totalVehicleOrderFees: number;
	totalPossibleVehicleOrderFees: number;
	totalPossibleVIPReservationFees: number;
	totalFinancingDownpayment: number;
	totalLooseItemCosts: number;
	totalLooseItemDiscounts: number;
	totalPossibleFinancingDownpayment: number;
	totalDiscount: number;
	totalPossibleFullPaymentDiscount: number;
	totalPossibleKlarnaFullPaymentDiscount: number;
	totalVehicleTaxes: number;
	totalPrevPaid: number;
	totalOtherFees: number;
	totalDefaultFees: number;
	totalFees: number;
}

interface CompleteTotals extends Totals {
	subtotal: number;
	totalCartCost: number;
	totalDueToday: number;
	appliedDiscounts: Record<string, number>;
	perVehicleCosts: Record<string, VehicleCost>;
	perLooseAccessoryCosts: Record<string, LooseAccessoryCost>;
	perAccessoryCosts: Record<string, AccessoryCost>;
	totalCartCostsPreTaxesFeesAndShipping: number;
	totalCartCostsPreTaxesAndFees: number;
	totalCartCostWithoutFullPaymentDiscount: number;
	shippingFeesSet: boolean;
	pickUpFeesSet: boolean;
}

// can be reused anywhere (firebase.getInvoice() -> new PaymentInvoiceTotals(paymentInvoice.lineItems))
export class PaymentInvoiceTotals {
	categories: Categories;
	metadata: Record<string, any>;

	constructor(categories: Categories = {}, metadata: Record<string, any> = {}) {
		this.categories = categories;
		this.metadata = metadata;
	}

	getLineItemTotalForCode(categoryKey: string, groupKey: string, code: string): number {
		if (!this.categories[categoryKey]?.[groupKey]) return 0;
		return this.categories[categoryKey][groupKey]
			.filter((item) => item.code === code)
			.reduce((sum, item) => sum + item.amount * (item?.quantity || 1), 0);
	}

	private _getSelectedPaymentOption(): string {
		return this.metadata?.selectedPaymentOption;
	}

	// eslint-disable-next-line class-methods-use-this
	private _computeDiscounts(
		items: LineItem[],
		selectedPaymentOption: string,
	): Record<string, number> {
		const discounts: Record<string, number> = {};
		(items ?? []).forEach((item) => {
			if (
				[
					LINE_ITEM_CODES.DISCOUNT,
					LINE_ITEM_CODES.FULL_PAYMENT_DISCOUNT,
					LINE_ITEM_CODES.KLARNA_DISCOUNT,
				].includes(item.code)
			) {
				if (
					(item.code === LINE_ITEM_CODES.FULL_PAYMENT_DISCOUNT &&
						selectedPaymentOption !== PAYMENT_CHOICES.CHECKOUT_FULL_PAYMENT) ||
					(item.code === LINE_ITEM_CODES.KLARNA_DISCOUNT &&
						selectedPaymentOption !== PAYMENT_CHOICES.KLARNA_CHECKOUT_FULL_PAYMENT)
				) {
					return;
				}
				const { discountId } = item.metadata || {};
				if (discountId) {
					discounts[discountId] =
						(discounts[discountId] || 0) + -item.amount * (item?.quantity || 1);
				}
			}
		});
		return discounts;
	}

	getPerVehicleCosts(): Record<string, VehicleCost> {
		const categoryKey = LINE_ITEM_CATEGORIES.VEHICLES;
		if (!this.categories[categoryKey]) return {};
		const selectedPaymentOption = this._getSelectedPaymentOption();

		return Object.keys(this.categories[categoryKey]).reduce((acc, groupKey) => {
			const basePrice = this.getLineItemTotalForCode(
				categoryKey,
				groupKey,
				LINE_ITEM_CODES.MSRP,
			);
			const specs = this.getLineItemTotalForCode(categoryKey, groupKey, LINE_ITEM_CODES.SPEC);
			const accessories = this.getLineItemTotalForCode(
				categoryKey,
				groupKey,
				LINE_ITEM_CODES.ACCESSORY,
			);
			const boltOnAccessories = this.getLineItemTotalForCode(
				categoryKey,
				groupKey,
				LINE_ITEM_CODES.BOLT_ON_ACCESSORY,
			);
			const preDiscountTotalPrice = basePrice + specs + accessories + boltOnAccessories;

			const discounts = this._computeDiscounts(
				this.categories[categoryKey][groupKey],
				selectedPaymentOption,
			);
			const discountSum = Object.values(discounts).reduce((sum, amount) => sum + amount, 0);
			const appliedDiscounts = Object.entries(discounts).map(([discountId, amount]) => ({
				discountId,
				amount,
			}));
			const totalPrice = preDiscountTotalPrice - discountSum;
			if (totalPrice < 0) {
				// createLog(
				// 	'Invalid-discount-amount',
				// 	'Discount amount is greater than the total price',
				// 	'CRITICAL',
				// 	{ vehicleId: groupKey, appliedDiscounts, preDiscountTotalPrice },
				// );
				throw new Error(
					`Discount amount (${discountSum}) is greater than the total price (${preDiscountTotalPrice}) for vehicle ${groupKey}`,
				);
			}
			acc[groupKey] = {
				preDiscountTotalPrice,
				perVehicleDiscounts: appliedDiscounts,
				totalPrice,
				basePrice,
				specs,
				accessories,
				boltOnAccessories,
				shippingFee: this.getLineItemTotalForCode(
					categoryKey,
					groupKey,
					LINE_ITEM_CODES.SHIPPING_FEE,
				),
				pickUpFee: this.getLineItemTotalForCode(
					categoryKey,
					groupKey,
					LINE_ITEM_CODES.PICKUP_FEE,
				),
				tempTagFee: this.getLineItemTotalForCode(
					categoryKey,
					groupKey,
					LINE_ITEM_CODES.TEMP_TAG_FEE,
				),
				totalPossibleRegistrationFees: this.getLineItemTotalForCode(
					categoryKey,
					groupKey,
					LINE_ITEM_CODES.REGISTRATION_FEE,
				),
				salesTax:
					this.getLineItemTotalForCode(
						categoryKey,
						groupKey,
						LINE_ITEM_CODES.VEHICLE_SALES_TAX,
					) +
					this.getLineItemTotalForCode(categoryKey, groupKey, LINE_ITEM_CODES.STATE_TAX),
				discounts: discountSum,
				downPayment: this.getLineItemTotalForCode(
					categoryKey,
					groupKey,
					LINE_ITEM_CODES.FINANCING_DOWNPAYMENT,
				),
				perVehiclePrevPaymentSum: -this.getLineItemTotalForCode(
					categoryKey,
					groupKey,
					LINE_ITEM_CODES.PREV_PAID,
				),
				orderFee: this.getLineItemTotalForCode(
					categoryKey,
					groupKey,
					LINE_ITEM_CODES.VEHICLE_ORDER_FEE,
				),
				vipReservationFee: this.getLineItemTotalForCode(
					categoryKey,
					groupKey,
					LINE_ITEM_CODES.VIP_RESERVATION_FEE,
				),
				otherFees:
					this.getLineItemTotalForCode(categoryKey, groupKey, LINE_ITEM_CODES.STATE_FEE) +
					this.getLineItemTotalForCode(categoryKey, groupKey, LINE_ITEM_CODES.VENDOR_FEE),
				defaultFees: this.categories[categoryKey][groupKey]
					.filter((item) => !Object.values(LINE_ITEM_CODES).includes(item.code))
					.reduce((sum, item) => sum + item.amount * (item?.quantity || 1), 0),
			};
			return acc;
		}, {} as Record<string, VehicleCost>);
	}

	getPerLooseAccessoryCosts(): Record<string, LooseAccessoryCost> {
		const categoryKey = LINE_ITEM_CATEGORIES.LOOSE_ACCESSORIES;
		if (!this.categories[categoryKey]) return {};
		const selectedPaymentOption = this._getSelectedPaymentOption();

		return Object.keys(this.categories[categoryKey]).reduce((acc, groupKey) => {
			const basePrice = this.getLineItemTotalForCode(
				categoryKey,
				groupKey,
				LINE_ITEM_CODES.LOOSE_ACCESSORY,
			);
			const accessorySalesTax = this.getLineItemTotalForCode(
				categoryKey,
				groupKey,
				LINE_ITEM_CODES.LOOSE_ACCESSORY_TAX,
			);
			const discounts = this._computeDiscounts(
				this.categories[categoryKey][groupKey],
				selectedPaymentOption,
			);
			const discountSum = Object.values(discounts).reduce<number>(
				(sum, amount) => sum + amount,
				0,
			);
			const appliedDiscounts = Object.entries(discounts).map(([discountId, amount]) => ({
				discountId,
				amount,
			}));
			const preDiscountTotalPrice = basePrice;
			const totalPrice = preDiscountTotalPrice - discountSum;
			if (totalPrice < 0) {
				// createLog(
				// 	'Invalid-discount-amount',
				// 	'Discount amount is greater than the total price for accessory',
				// 	'CRITICAL',
				// 	{ accessoryId: groupKey, appliedDiscounts, preDiscountTotalPrice },
				// );
				throw new Error(
					`Discount amount (${discountSum}) is greater than the total price (${preDiscountTotalPrice}) for accessory ${groupKey}`,
				);
			}
			acc[groupKey] = {
				preDiscountTotalPrice,
				totalPrice,
				basePrice,
				accessorySalesTax,
				discounts: discountSum,
				appliedDiscounts,
			};
			return acc;
		}, {} as Record<string, LooseAccessoryCost>);
	}

	getPerAccessoryCosts(): Record<string, AccessoryCost> {
		const accessoryCosts: Record<
			string,
			{ preDiscountTotalPrice: number; discountTotal: number }
		> = {};

		const addToAccessoryGroup = (
			accessoryId: string,
			amountToAdd: number,
			isDiscount = false,
		): void => {
			if (!accessoryId) return;
			if (!accessoryCosts[accessoryId]) {
				accessoryCosts[accessoryId] = { preDiscountTotalPrice: 0, discountTotal: 0 };
			}
			if (isDiscount) {
				// Discount amounts are assumed to be negative in the line items,
				// so we add the absolute value (multiplied by quantity) to get a positive discount.
				accessoryCosts[accessoryId].discountTotal += Math.abs(amountToAdd);
			} else {
				accessoryCosts[accessoryId].preDiscountTotalPrice += amountToAdd;
			}
		};

		const processedVehicleAccessories = new Set<string>();
		const processedVehicleDiscounts = new Set<string>();

		const vehiclesCategory = this.categories[LINE_ITEM_CATEGORIES.VEHICLES];
		if (vehiclesCategory) {
			Object.keys(vehiclesCategory).forEach((vehicleGroup) => {
				vehiclesCategory[vehicleGroup].forEach((item) => {
					if (
						item.code === LINE_ITEM_CODES.ACCESSORY ||
						item.code === LINE_ITEM_CODES.BOLT_ON_ACCESSORY
					) {
						const accessoryId = item.metadata?.accessoryId;
						if (accessoryId && !processedVehicleAccessories.has(accessoryId)) {
							processedVehicleAccessories.add(accessoryId);
							addToAccessoryGroup(accessoryId, item.amount * (item.quantity || 1));
						}
					}
					if (
						[
							LINE_ITEM_CODES.DISCOUNT,
							LINE_ITEM_CODES.FULL_PAYMENT_DISCOUNT,
							LINE_ITEM_CODES.KLARNA_DISCOUNT,
						].includes(item.code)
					) {
						const accessoryId = item.metadata?.accessoryId;
						if (accessoryId && !processedVehicleDiscounts.has(accessoryId)) {
							processedVehicleDiscounts.add(accessoryId);
							addToAccessoryGroup(
								accessoryId,
								item.amount * (item.quantity || 1),
								true,
							);
						}
					}
				});
			});
		}

		const looseCategory = this.categories[LINE_ITEM_CATEGORIES.LOOSE_ACCESSORIES];
		if (looseCategory) {
			Object.keys(looseCategory).forEach((looseGroup) => {
				looseCategory[looseGroup].forEach((item) => {
					if (item.code === LINE_ITEM_CODES.LOOSE_ACCESSORY) {
						const accessoryId = item.metadata?.accessoryId;
						addToAccessoryGroup(accessoryId, item.amount * (item.quantity || 1));
					}
					if (
						[
							LINE_ITEM_CODES.DISCOUNT,
							LINE_ITEM_CODES.FULL_PAYMENT_DISCOUNT,
							LINE_ITEM_CODES.KLARNA_DISCOUNT,
						].includes(item.code)
					) {
						const accessoryId = item.metadata?.accessoryId;
						if (accessoryId) {
							addToAccessoryGroup(
								accessoryId,
								item.amount * (item.quantity || 1),
								true,
							);
						}
					}
				});
			});
		}

		const perAccessoryCosts: Record<string, AccessoryCost> = {};
		Object.keys(accessoryCosts).forEach((accessoryId) => {
			const { preDiscountTotalPrice, discountTotal } = accessoryCosts[accessoryId];
			let totalPrice = preDiscountTotalPrice - discountTotal;
			if (totalPrice < 0) {
				// createLog(
				// 	'Invalid-discount-amount',
				// 	'Discount amount is greater than the total price for accessory',
				// 	'CRITICAL',
				// 	{ accessoryId, discountTotal, preDiscountTotalPrice },
				// );
				totalPrice = 0;
			}
			perAccessoryCosts[accessoryId] = {
				preDiscountTotalPrice,
				totalPrice,
			};
		});

		return perAccessoryCosts;
	}

	private _hasShippingFees(): boolean {
		const categoryKey = LINE_ITEM_CATEGORIES.VEHICLES;
		if (!this.categories[categoryKey]) return false;
		return Object.values(this.categories[categoryKey]).some((group) =>
			group.some((item) => item.code === LINE_ITEM_CODES.SHIPPING_FEE),
		);
	}

	private _hasPickupFees(): boolean {
		const categoryKey = LINE_ITEM_CATEGORIES.VEHICLES;
		if (!this.categories[categoryKey]) return false;
		return Object.values(this.categories[categoryKey]).some((group) =>
			group.some((item) => item.code === LINE_ITEM_CODES.PICKUP_FEE),
		);
	}

	private _getAppliedDiscounts(): Record<string, number> {
		const selectedPaymentOption = this._getSelectedPaymentOption();
		const discounts: Record<string, number> = {};
		Object.keys(this.categories).forEach((categoryKey) => {
			Object.keys(this.categories[categoryKey]).forEach((groupKey) => {
				const groupDiscounts = this._computeDiscounts(
					this.categories[categoryKey][groupKey],
					selectedPaymentOption,
				);
				Object.entries(groupDiscounts).forEach(([discountId, amount]) => {
					discounts[discountId] = (discounts[discountId] || 0) + amount;
				});
			});
		});
		return discounts;
	}

	getGrandTotal(): number {
		let grandTotal = 0;
		Object.keys(this.categories).forEach((categoryKey) => {
			Object.keys(this.categories[categoryKey]).forEach((groupKey) => {
				grandTotal += this.categories[categoryKey][groupKey].reduce(
					(sum, item) => sum + item.amount * (item.quantity || 1),
					0,
				);
			});
		});
		return grandTotal;
	}

	getTotals(): CompleteTotals {
		const perVehicle = this.getPerVehicleCosts();
		const perLoose = this.getPerLooseAccessoryCosts();

		// Subtotal: sum of base vehicle components and loose accessory base prices.
		let subtotal = 0;
		Object.values(perVehicle).forEach((vehicle) => {
			subtotal +=
				vehicle.basePrice + vehicle.specs + vehicle.accessories + vehicle.boltOnAccessories;
		});
		Object.values(perLoose).forEach((accessory) => {
			subtotal += accessory.basePrice;
		});

		const totals: Totals = {
			totalShippingFees: 0,
			totalPickUpFees: 0,
			totalTaxes: 0,
			totalLooseAccessoryTaxes: 0,
			totalRegistrationFees: 0,
			totalTempTagFees: 0,
			totalVehicleOrderFees: 0, // todo
			totalPossibleVehicleOrderFees: 0,
			totalPossibleVIPReservationFees: 0,
			totalFinancingDownpayment: 0,
			totalLooseItemCosts: 0,
			totalLooseItemDiscounts: 0,
			totalPossibleFinancingDownpayment: 0,
			totalDiscount: 0,
			totalPossibleFullPaymentDiscount: 0,
			totalPossibleKlarnaFullPaymentDiscount: 0,

			totalVehicleTaxes: 0,
			totalPrevPaid: 0,
			totalOtherFees: 0,
			totalDefaultFees: 0,
			totalFees: 0,
		};

		Object.keys(this.categories).forEach((categoryKey) => {
			Object.keys(this.categories[categoryKey]).forEach((groupKey) => {
				(this.categories[categoryKey][groupKey] ?? []).forEach((item) => {
					const amt = item.amount * (item.quantity || 1);
					switch (item.code) {
						case LINE_ITEM_CODES.REGISTRATION_FEE:
							totals.totalRegistrationFees += amt;
							totals.totalFees += amt;
							break;
						case LINE_ITEM_CODES.STATE_TAX:
							totals.totalTaxes += amt;
							totals.totalVehicleTaxes += amt;
							break;
						case LINE_ITEM_CODES.VEHICLE_SALES_TAX:
						case LINE_ITEM_CODES.LOOSE_ACCESSORY_TAX:
							totals.totalTaxes += amt;
							if (item.code === LINE_ITEM_CODES.VEHICLE_SALES_TAX) {
								totals.totalVehicleTaxes += amt;
							} else {
								totals.totalLooseAccessoryTaxes += amt;
							}
							break;
						case LINE_ITEM_CODES.SHIPPING_FEE:
							totals.totalShippingFees += amt;
							break;
						case LINE_ITEM_CODES.PICKUP_FEE:
							totals.totalPickUpFees += amt;
							break;
						case LINE_ITEM_CODES.TEMP_TAG_FEE:
							totals.totalTempTagFees += amt;
							totals.totalFees += amt;
							break;
						case LINE_ITEM_CODES.DISCOUNT:
							totals.totalDiscount += -amt;
							if (categoryKey === LINE_ITEM_CATEGORIES.LOOSE_ACCESSORIES) {
								totals.totalLooseItemDiscounts += -amt;
							}
							break;
						case LINE_ITEM_CODES.FULL_PAYMENT_DISCOUNT:
							totals.totalPossibleFullPaymentDiscount += -amt;
							break;
						case LINE_ITEM_CODES.KLARNA_DISCOUNT:
							totals.totalPossibleKlarnaFullPaymentDiscount += -amt;
							break;
						case LINE_ITEM_CODES.FINANCING_DOWNPAYMENT:
							totals.totalFinancingDownpayment += amt;
							totals.totalPossibleFinancingDownpayment += amt;
							break;
						case LINE_ITEM_CODES.PREV_PAID:
							totals.totalPrevPaid += -amt;
							break;
						case LINE_ITEM_CODES.VEHICLE_ORDER_FEE:
							totals.totalPossibleVehicleOrderFees += amt;
							break;
						case LINE_ITEM_CODES.VIP_RESERVATION_FEE:
							totals.totalPossibleVIPReservationFees += amt;
							break;
						case LINE_ITEM_CODES.STATE_FEE:
						case LINE_ITEM_CODES.VENDOR_FEE:
							totals.totalFees += amt;
							totals.totalOtherFees += amt;
							break;
						default:
							if (
								categoryKey === LINE_ITEM_CATEGORIES.LOOSE_ACCESSORIES &&
								item.code === LINE_ITEM_CODES.LOOSE_ACCESSORY
							) {
								totals.totalLooseItemCosts += amt;
							}
							if (!Object.values(LINE_ITEM_CODES).includes(item.code)) {
								totals.totalDefaultFees += amt;
							}
					}
				});
			});
		});

		const adjustedSubtotal = subtotal - totals.totalPrevPaid;

		const totalCartCostsPreTaxesFeesAndShipping = adjustedSubtotal - totals.totalDiscount;
		const totalCartCostsPreTaxesAndFees =
			totalCartCostsPreTaxesFeesAndShipping +
			totals.totalShippingFees +
			totals.totalPickUpFees;
		const totalCartCostWithoutFullPaymentDiscount =
			adjustedSubtotal +
			totals.totalShippingFees +
			totals.totalPickUpFees +
			totals.totalRegistrationFees +
			totals.totalTempTagFees +
			totals.totalTaxes +
			totals.totalOtherFees +
			totals.totalDefaultFees;

		const selectedPaymentOption = this._getSelectedPaymentOption();
		let computedTotalCartCost = 0;
		let computedDueToday = 0;

		switch (selectedPaymentOption) {
			case PAYMENT_CHOICES.LOAN_DOWNPAYMENT:
				computedTotalCartCost = Math.max(
					adjustedSubtotal +
						totals.totalShippingFees +
						totals.totalPickUpFees +
						totals.totalRegistrationFees +
						totals.totalTempTagFees -
						totals.totalDiscount +
						totals.totalTaxes +
						totals.totalOtherFees +
						totals.totalDefaultFees,
					0,
				);
				computedDueToday =
					totals.totalFinancingDownpayment +
					(totals.totalLooseItemCosts -
						totals.totalLooseItemDiscounts +
						totals.totalLooseAccessoryTaxes);
				break;
			case PAYMENT_CHOICES.CHECKOUT_FULL_PAYMENT:
				computedTotalCartCost = Math.max(
					adjustedSubtotal +
						totals.totalShippingFees +
						totals.totalPickUpFees +
						totals.totalRegistrationFees +
						totals.totalTempTagFees -
						totals.totalDiscount -
						totals.totalPossibleFullPaymentDiscount +
						totals.totalTaxes +
						totals.totalOtherFees +
						totals.totalDefaultFees,
					0,
				);
				computedDueToday = computedTotalCartCost;
				break;
			case PAYMENT_CHOICES.KLARNA_CHECKOUT_FULL_PAYMENT:
				computedTotalCartCost = Math.max(
					adjustedSubtotal +
						totals.totalShippingFees +
						totals.totalPickUpFees +
						totals.totalRegistrationFees +
						totals.totalTempTagFees -
						totals.totalDiscount -
						totals.totalPossibleKlarnaFullPaymentDiscount +
						totals.totalTaxes +
						totals.totalOtherFees +
						totals.totalDefaultFees,
					0,
				);
				computedDueToday = computedTotalCartCost;
				break;
			case PAYMENT_CHOICES.STANDARD_RESERVATION:
				computedTotalCartCost = Math.max(
					adjustedSubtotal +
						totals.totalShippingFees +
						totals.totalPickUpFees +
						totals.totalRegistrationFees +
						totals.totalTempTagFees -
						totals.totalDiscount +
						totals.totalTaxes +
						totals.totalOtherFees +
						totals.totalDefaultFees,
					0,
				);
				computedDueToday =
					totals.totalPossibleVehicleOrderFees +
					(totals.totalLooseItemCosts -
						totals.totalLooseItemDiscounts +
						totals.totalLooseAccessoryTaxes);
				break;
			case PAYMENT_CHOICES.PREORDER_RESERVATION:
				computedTotalCartCost = adjustedSubtotal;
				computedDueToday = totals.totalPossibleVehicleOrderFees;
				break;
			case PAYMENT_CHOICES.PREORDER_VIP_RESERVATION:
				computedTotalCartCost = adjustedSubtotal;
				computedDueToday = totals.totalPossibleVIPReservationFees;
				break;
			default:
				// Fallback: if payment option is unknown, use full totals.
				computedTotalCartCost = this.getGrandTotal();
				computedDueToday = computedTotalCartCost;
		}

		return {
			subtotal: adjustedSubtotal,
			totalCartCost: computedTotalCartCost,
			totalDueToday: computedDueToday,
			appliedDiscounts: this._getAppliedDiscounts(),
			...totals,
			perVehicleCosts: perVehicle,
			perLooseAccessoryCosts: perLoose,
			perAccessoryCosts: this.getPerAccessoryCosts(),
			totalCartCostsPreTaxesFeesAndShipping,
			totalCartCostsPreTaxesAndFees,
			totalCartCostWithoutFullPaymentDiscount,
			shippingFeesSet: this._hasShippingFees(),
			pickUpFeesSet: this._hasPickupFees(),
		};
	}
}
